import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
} from '@mui/material';

function numberWithCommas(x) {
  if (!x && x !== 0) return 0;
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const getDeltaCellStyle = (delta, baseline) => {
  const absPct =
    Math.abs(baseline) > 0 ? Math.abs(delta) / Math.abs(baseline) : 0;
  if (absPct >= 0.1) {
    return { backgroundColor: '#ff000042' }; // red
  } else if (absPct >= 0.05) {
    return { backgroundColor: '#fff44e8f' }; // yellow
  } else {
    return {}; // no highlight
  }
};

const getDeltaTextStyle = (delta) => {
  if (delta > 0) {
    return { fontWeight: 'bold', color: 'green' };
  } else if (delta < 0) {
    return { fontWeight: 'bold', color: 'red' };
  } else {
    return { fontWeight: 'bold' };
  }
};

const hasSignificantDelta = (clientData, thresholds) => {
  const salesBaseline = clientData.radSales || 1;
  const tipsBaseline = clientData.radTips || 1;
  const laborBaseline = clientData.radLabor || 1;

  const salesDeltas = [
    clientData.deltaRadToastSales,
    clientData.deltaRadDayforceSales,
  ];

  const tipsDeltas = [
    clientData.deltaRadExpectedTips,
    clientData.deltaDayforceExpectedTips,
  ];

  const laborDeltas = [clientData.deltaRadDayforceLabor];

  const anySalesDeltaHigh = salesDeltas.some(
    (d) => Math.abs(d) / Math.abs(salesBaseline) >= thresholds.sales
  );
  const anyTipsDeltaHigh = tipsDeltas.some(
    (d) => Math.abs(d) / Math.abs(tipsBaseline) >= thresholds.tips
  );
  const anyLaborDeltaHigh = laborDeltas.some(
    (d) => Math.abs(d) / Math.abs(laborBaseline) >= thresholds.labor
  );

  return anySalesDeltaHigh || anyTipsDeltaHigh || anyLaborDeltaHigh;
};

const ClientTable = ({
  rows,
  order,
  orderBy,
  handleRequestSort,
  columnMap,
  thresholds,
  filterSignificantOnly,
}) => {
  const displayedRows = rows;

  const handleSortRequest = (property) => (event) => {
    handleRequestSort(event, property);
  };

  const Row = ({ clientData, index }) => {
    const significant = hasSignificantDelta(clientData, thresholds);

    const computedRowColor = significant
      ? '#ffe0e0'
      : index % 2 === 0
      ? '#f9f9f9'
      : '#ffffff';
    const computedHoverColor = significant
      ? '#ffcccc'
      : index % 2 === 0
      ? '#f1f1f1'
      : '#f5f5f5';

    return (
      <TableRow
        sx={{
          backgroundColor: computedRowColor,
          '&:hover': { backgroundColor: computedHoverColor },
        }}
      >
        <TableCell>{clientData.date}</TableCell>
        <TableCell>{clientData.storeId}</TableCell>
        <TableCell>
          ${numberWithCommas(Number(clientData.toastSales).toFixed(2))}
        </TableCell>
        <TableCell>
          ${numberWithCommas(Number(clientData.dayforceSales).toFixed(2))}
        </TableCell>
        <TableCell>
          ${numberWithCommas(Number(clientData.radSales).toFixed(2))}
        </TableCell>

        {/* Delta Sales */}
        <TableCell
          style={getDeltaCellStyle(
            clientData.deltaRadToastSales,
            clientData.radSales
          )}
        >
          <div style={getDeltaTextStyle(clientData.deltaRadToastSales)}>
            $
            {numberWithCommas(Number(clientData.deltaRadToastSales).toFixed(2))}
          </div>
        </TableCell>
        <TableCell
          style={getDeltaCellStyle(
            clientData.deltaRadDayforceSales,
            clientData.radSales
          )}
        >
          <div style={getDeltaTextStyle(clientData.deltaRadDayforceSales)}>
            $
            {numberWithCommas(
              Number(clientData.deltaRadDayforceSales).toFixed(2)
            )}
          </div>
        </TableCell>

        <TableCell>
          ${numberWithCommas(Number(clientData.toastTips).toFixed(2))}
        </TableCell>
        <TableCell>
          ${numberWithCommas(Number(clientData.oloTips).toFixed(2))}
        </TableCell>
        <TableCell>
          ${numberWithCommas(Number(clientData.dayforceTips).toFixed(2))}
        </TableCell>
        <TableCell>
          ${numberWithCommas(Number(clientData.radTips).toFixed(2))}
        </TableCell>

        {/* Delta Tips */}
        <TableCell
          style={getDeltaCellStyle(
            clientData.deltaRadExpectedTips,
            clientData.radTips
          )}
        >
          <div style={getDeltaTextStyle(clientData.deltaRadExpectedTips)}>
            $
            {numberWithCommas(
              Number(clientData.deltaRadExpectedTips).toFixed(2)
            )}
          </div>
        </TableCell>
        <TableCell
          style={getDeltaCellStyle(
            clientData.deltaDayforceExpectedTips,
            clientData.radTips
          )}
        >
          <div style={getDeltaTextStyle(clientData.deltaDayforceExpectedTips)}>
            $
            {numberWithCommas(
              Number(clientData.deltaDayforceExpectedTips).toFixed(2)
            )}
          </div>
        </TableCell>

        <TableCell>
          {numberWithCommas(Number(clientData.dayforceLabor).toFixed(2))}
        </TableCell>
        <TableCell>
          {numberWithCommas(Number(clientData.radLabor).toFixed(2))}
        </TableCell>

        {/* Delta Labor */}
        <TableCell
          style={getDeltaCellStyle(
            clientData.deltaRadDayforceLabor,
            clientData.radLabor
          )}
        >
          <div style={getDeltaTextStyle(clientData.deltaRadDayforceLabor)}>
            {numberWithCommas(
              Number(clientData.deltaRadDayforceLabor).toFixed(2)
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        maxHeight: 'calc(100vh - 300px)',
        overflowY: 'auto',
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {[
              'date',
              'storeId',
              'toastSales',
              'dayforceSales',
              'radSales',
              'deltaRadToastSales',
              'deltaRadDayforceSales',
              'toastTips',
              'oloTips',
              'dfTips',
              'radTips',
              'deltaRadExpectedTips',
              'deltaDayforceExpectedTips',
              'dfLabor',
              'radLabor',
              'deltaRadDayforceLabor',
            ].map((column) => (
              <TableCell
                key={column}
                sx={{
                  position: 'sticky',
                  top: 0,
                  backgroundColor: '#f8f8f8',
                  zIndex: 1,
                }}
              >
                <TableSortLabel
                  active={orderBy === column}
                  direction={orderBy === column ? order : 'asc'}
                  onClick={handleSortRequest(column)}
                >
                  {columnMap[column]}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedRows
            ?.filter((clientData) => {
              if (!filterSignificantOnly) return true;
              return hasSignificantDelta(clientData, thresholds);
            })
            .map((clientData, index) => (
              <Row key={index} clientData={clientData} index={index} />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ClientTable;
